import { ICustomFields } from '@llws/lift-entity-interfaces';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IApiResponse } from '@/shared';

/**
 * Fetches Custom Content <br>
 * API Endpoint:
 * ```
 * /custom-content/${tableName}/${objectId}?`
 *     + `${(modelType ? `modelType=${modelType},${parent}` : ``)}`
 *     + `${(clientId && modelType ? '&' : '')}`
 *     + `${(clientId ? `clientId=${clientId}` : ``)}
 * ``` via {@link adminRequest.get}
 * @param {number} objectId - id of the object to get
 * @param {string} tableName - name of the table
 * @param {string} [modelType] - optional type of the model
 * @param {string} [parent] - optional parent of the model
 * @param {(number | string)} [clientId] - optional id of the client
 * @param {number} siteIds - optional site ids to get content based on sites
 * @param {number} siteClientIds - optional client ids to link to siteIds
 */
export const get = (
  objectId: number,
  tableName: string,
  modelType?: string,
  parent?: string,
  clientId?: number | string,
  customFieldCategories?: string,
  siteIds?: number,
  siteClientIds?: number,
) => {
  const querystring: string = [
    ...modelType ? [`modelType=${modelType}${(parent ? `,${parent}` : '')}`] : [],
    ...clientId ? [`clientId=${clientId}`] : [],
    ...customFieldCategories ? [`customFieldCategories=${customFieldCategories}`] : [],
    ...siteIds ? [`siteIds=${siteIds}`] : [],
    ...siteClientIds ? [`siteClientIds=${siteClientIds}`] : [],
  ].join('&');

  return adminRequest.get(`/custom-content/${tableName}/${objectId}?${querystring}`);
};

/**
 * Saves Custom Field <br>
 * API Endpoint - `/property-api/custom-fields/${id}` <br> via {@link adminRequest.put}
 * @param {ICustomFields} data - Custom Field to save
 * @param {number} id - id of the custom field
 */
export const saveCustomField = (
  data: ICustomFields,
  id: number,
): Promise<IApiResponse<ICustomFields, any>> => adminRequest.put(`/property-api/custom-fields/${id}`, data);

/**
 * Adds New Custom Field <br>
 * API Endpoint - `/property-api/custom-fields` <br> via {@link adminRequest.post}
 */
export const addCustomFields = (
  data: ICustomFields[],
): Promise<IApiResponse<ICustomFields[], any>> => adminRequest.post(`/property-api/custom-fields`, data);

export default {
  get,
  saveCustomField,
  addCustomFields,
};
